import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';

import API_BASE from '../../utils/api';
const API = API_BASE;

const examTypeLabels = {
  unit_test: 'Unit Test', mid_term: 'Mid Term', quarterly: 'Quarterly',
  half_yearly: 'Half Yearly', annual: 'Annual', assignment: 'Assignment'
};

const Exams = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [report, setReport] = useState(null);
  const [exams, setExams] = useState([]);
  const [pendingExams, setPendingExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes`, { headers });
      setClasses(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exams/review/pending`, { headers });
      setPendingExams(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchExams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exams`, { headers });
      setExams(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchClasses(); fetchPending(); fetchExams(); }, [fetchClasses, fetchPending, fetchExams]);

  const fetchReport = async (classId) => {
    if (!classId) { setReport(null); return; }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/exams/report/${classId}`, { headers });
      setReport(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📊 Exam Reports</h1>
              <p className="text-gray-500 mt-1">View all exam results and class reports</p>
            </div>
            <ProfileDropdown />
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
            <button onClick={() => setActiveTab('overview')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'overview' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600'}`}>
              Overview
            </button>
            <button onClick={() => setActiveTab('report')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === 'report' ? 'bg-white text-indigo-700 shadow' : 'text-gray-600'}`}>
              Class Report
            </button>
          </div>

          {/* Overview */}
          {activeTab === 'overview' && (
            <div>
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{exams.length}</p>
                  <p className="text-sm text-blue-600">Total Exams</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-700">{pendingExams.filter(e => e.status === 'submitted').length}</p>
                  <p className="text-sm text-yellow-600">Pending Review</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{pendingExams.filter(e => e.status === 'approved').length}</p>
                  <p className="text-sm text-green-600">Approved</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">{classes.length}</p>
                  <p className="text-sm text-purple-600">Classes</p>
                </div>
              </div>

              {/* All Exams Table */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">All Exams</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">#</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Exam Name</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Type</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Subject</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Class</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Date</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Total Marks</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Created By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exams.map((e, i) => (
                        <tr key={e._id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{i + 1}</td>
                          <td className="p-3 font-medium">{e.examName}</td>
                          <td className="p-3"><span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full">{examTypeLabels[e.examType]}</span></td>
                          <td className="p-3 text-sm">{e.subject}</td>
                          <td className="p-3 text-sm">{e.classId?.className} - {e.classId?.section}</td>
                          <td className="p-3 text-sm">{new Date(e.date).toLocaleDateString()}</td>
                          <td className="p-3 text-sm">{e.totalMarks}</td>
                          <td className="p-3 text-sm">{e.createdBy?.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {exams.length === 0 && <p className="text-center py-8 text-gray-400">No exams found</p>}
                </div>
              </div>
            </div>
          )}

          {/* Class Report */}
          {activeTab === 'report' && (
            <div>
              <div className="flex gap-4 items-end mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Class</label>
                  <select value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); fetchReport(e.target.value); }}
                    className="border rounded-lg px-3 py-2 w-64">
                    <option value="">Choose a class</option>
                    {classes.map(c => <option key={c._id} value={c._id}>{c.className} - {c.section}</option>)}
                  </select>
                </div>
              </div>

              {loading ? <p className="text-center py-10 text-gray-500">Loading...</p> :
                report ? (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                      Class Report — {report.exams.length} Exams, {report.report.length} Students
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="p-2 font-semibold text-gray-600 sticky left-0 bg-gray-50">#</th>
                            <th className="p-2 font-semibold text-gray-600 sticky left-8 bg-gray-50 min-w-[100px]">Roll No</th>
                            <th className="p-2 font-semibold text-gray-600 sticky left-28 bg-gray-50 min-w-[150px]">Student</th>
                            {report.exams.map(ex => (
                              <th key={ex._id} className="p-2 font-semibold text-gray-600 text-center min-w-[120px]">
                                <div>{ex.examName}</div>
                                <div className="text-xs text-gray-400 font-normal">{ex.subject} ({ex.totalMarks})</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {report.report.map((row, i) => (
                            <tr key={row.student._id} className="border-b hover:bg-gray-50">
                              <td className="p-2 sticky left-0 bg-white">{i + 1}</td>
                              <td className="p-2 font-medium sticky left-8 bg-white">{row.student.rollNumber}</td>
                              <td className="p-2 sticky left-28 bg-white">{row.student.name}</td>
                              {row.results.map((r, j) => (
                                <td key={j} className={`p-2 text-center font-medium ${r.marksObtained === '-' ? 'text-gray-300' : r.grade === 'F' ? 'text-red-600' : 'text-gray-800'}`}>
                                  {r.marksObtained === '-' ? '-' : `${r.marksObtained} (${r.grade})`}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {report.report.length === 0 && <p className="text-center py-8 text-gray-400">No students in this class</p>}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-5xl mb-3">📊</p>
                    <p>Select a class to view the report</p>
                  </div>
                )
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exams;
